/**
 * Stage 5: Review - Experience Creation Wizard
 *
 * Final review and publish stage:
 * - Full experience preview (as guests will see it)
 * - Validation checklist (required + recommended)
 * - Terms acknowledgment
 * - Publish vs Save as Draft
 * - Celebration modal on success
 * - Photos loaded from assets API (SCRUM-783 AC-07)
 *
 * Epic: MVP 1.27 - Experience Creation Wizard
 * Stories: SCRUM-208 to SCRUM-211, SCRUM-783
 * Location: apps/web/src/components/experiences/creation/stages/Stage5Review.jsx
 */

import { useState, useMemo, useEffect } from 'react';
import FormActions from '../../../common/forms/FormActions';
import { getAssetsByEntity, getAssetUrl } from '../../../../services/assetService';

/**
 * Stage5Review Component
 *
 * @param {Object} props
 * @param {Object} props.formData - Current wizard form data
 * @param {Function} props.onNext - Complete wizard (publish)
 * @param {Function} props.onBack - Navigate to previous stage
 * @param {Object} props.validation - Validation errors for current stage
 * @param {boolean} props.isValid - Whether current stage is valid
 * @param {string} [props.experienceId] - Experience UUID for loading assets from API
 */
export default function Stage5Review({
  formData,
  onNext,
  onBack,
  validation: _validation,
  isValid: _isValid,
  experienceId,
  isPublishing = false,
  isNewExperience = true
}) {
  // Local state
  const [_error, _setError] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [_showCelebration, _setShowCelebration] = useState(false);

  // AC-07: Photos loaded from assets API
  const [apiCoverPhoto, setApiCoverPhoto] = useState(null);
  const [apiGalleryPhotos, setApiGalleryPhotos] = useState([]);

  /**
   * AC-07: Load photos from assets API on mount.
   * Falls back to formData photos if API returns nothing.
   */
  useEffect(() => {
    let cancelled = false;
    const entityId = experienceId || formData._provisionalEntityId;

    async function loadPhotos() {
      if (!entityId) {
        return;
      }

      try {
        const assets = await getAssetsByEntity('experience', entityId);

        if (cancelled || !assets || assets.length === 0) {
          return;
        }

        let cover = null;
        const gallery = [];

        for (const asset of assets) {
          const url = getAssetUrl(asset, asset.asset_purpose === 'cover' ? 'large' : 'medium')
            || getAssetUrl(asset, 'original');

          const photoObj = {
            url,
            assetId: asset.id,
            caption: asset.caption || '',
          };

          if (asset.asset_purpose === 'cover' || asset.is_primary) {
            cover = photoObj;
          } else {
            gallery.push(photoObj);
          }
        }

        if (!cancelled) {
          if (cover) {
            setApiCoverPhoto(cover);
          }
          if (gallery.length > 0) {
            setApiGalleryPhotos(gallery);
          }
        }
      } catch {
        // Silently fall back to formData photos
      }
    }

    loadPhotos();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceId, formData._provisionalEntityId]);

  // Use API photos if available, otherwise fall back to formData
  const displayCoverPhoto = apiCoverPhoto || formData.coverPhoto;
  const displayGalleryPhotos = apiGalleryPhotos.length > 0 ? apiGalleryPhotos : (formData.galleryPhotos || []);

  /**
   * Calculate validation checklist - SCRUM-209
   */
  const validationChecklist = useMemo(() => {
    return [
      {
        label: 'Experience name',
        required: true,
        complete: Boolean(formData.experienceName && formData.experienceName.length >= 3),
        details: formData.experienceName || 'Not provided'
      },
      {
        label: 'Primary category (L1)',
        required: true,
        complete: Boolean(formData.categoryL1Id),
        details: formData.categoryL1Id ? 'Selected' : 'Not selected'
      },
      {
        label: 'Secondary category (L2)',
        required: true,
        complete: Boolean(formData.categoryL2Id),
        details: formData.categoryL2Id ? 'Selected' : 'Not selected'
      },
      {
        label: 'Description',
        required: true,
        complete: Boolean(formData.description && formData.description.length >= 50),
        details: formData.description ? `${formData.description.length} characters` : 'Not provided'
      },
      {
        label: 'Highlights',
        required: false,
        complete: formData.highlights?.length >= 3,
        details: `${formData.highlights?.length || 0} highlights (3+ recommended)`
      },
      {
        label: 'Meeting point',
        required: true,
        complete: Boolean(formData.meetingPointAddress),
        details: formData.meetingPointAddress || 'Not provided'
      },
      {
        label: 'Duration',
        required: true,
        complete: Boolean(formData.durationHours && formData.durationHours > 0),
        details: formData.durationHours ? `${formData.durationHours} hours` : 'Not set'
      },
      {
        label: 'Group size limits',
        required: true,
        complete: Boolean(formData.minGroupSize && formData.maxGroupSize),
        details: `${formData.minGroupSize || 0}-${formData.maxGroupSize || 0} guests`
      },
      {
        label: 'Pricing',
        required: true,
        complete: Boolean(
          formData.pricingModel &&
          (formData.basePriceCents >= 1000 || formData.perPersonPriceCents >= 1000)
        ),
        details: formData.pricingModel || 'Not set'
      },
      {
        label: 'Cover photo',
        required: true,
        complete: Boolean(displayCoverPhoto),
        details: displayCoverPhoto ? 'Uploaded' : 'Not uploaded'
      },
      {
        label: 'Gallery photos',
        required: false,
        complete: displayGalleryPhotos.length >= 3,
        details: `${displayGalleryPhotos.length} photos (3+ recommended)`
      }
    ];
  }, [formData, displayCoverPhoto, displayGalleryPhotos]);

  /**
   * Calculate completion stats
   */
  const stats = useMemo(() => {
    const required = validationChecklist.filter(item => item.required);
    const recommended = validationChecklist.filter(item => !item.required);

    const requiredComplete = required.filter(item => item.complete).length;
    const recommendedComplete = recommended.filter(item => item.complete).length;

    return {
      required: { total: required.length, complete: requiredComplete },
      recommended: { total: recommended.length, complete: recommendedComplete },
      allRequiredComplete: requiredComplete === required.length
    };
  }, [validationChecklist]);

  /**
   * Format price for display
   */
  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  /**
   * Handle publish
   */
  const handlePublish = () => {
    if (!termsAccepted) {
      _setError('Please accept the Terms of Service to publish your experience');
      setTimeout(() => _setError(null), 3000);
      return;
    }

    if (!stats.allRequiredComplete) {
      _setError('Please complete all required fields before publishing');
      setTimeout(() => _setError(null), 3000);
      return;
    }

    // Trigger onNext which calls onComplete in wizard
    onNext();
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Review & Publish</h2>
        <p className="text-gray-600 mb-6">
          Review your experience details and publish when ready. You can edit anything later from your dashboard.
        </p>

        {/* Validation Checklist - SCRUM-209 */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Validation Checklist
          </h3>

          {/* Required Items */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-700">
                Required ({stats.required.complete}/{stats.required.total})
              </h4>
              {stats.allRequiredComplete ? (
                <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded">
                  All complete
                </span>
              ) : (
                <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded">
                  {stats.required.total - stats.required.complete} missing
                </span>
              )}
            </div>

            <div className="space-y-2">
              {validationChecklist.filter(item => item.required).map((item, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 p-3 rounded-lg ${
                    item.complete ? 'bg-white border border-emerald-200' : 'bg-red-50 border border-red-200'
                  }`}
                >
                  {/* Checkmark or X */}
                  <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                    item.complete ? 'bg-emerald-600' : 'bg-red-600'
                  }`}>
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {item.complete ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                      )}
                    </svg>
                  </div>

                  {/* Item Details */}
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${
                      item.complete ? 'text-gray-900' : 'text-red-900'
                    }`}>
                      {item.label}
                    </div>
                    <div className={`text-xs ${
                      item.complete ? 'text-gray-600' : 'text-red-600'
                    }`}>
                      {item.details}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommended Items */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              Recommended ({stats.recommended.complete}/{stats.recommended.total})
            </h4>

            <div className="space-y-2">
              {validationChecklist.filter(item => !item.required).map((item, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 p-3 rounded-lg ${
                    item.complete ? 'bg-white border border-gray-200' : 'bg-amber-50 border border-amber-200'
                  }`}
                >
                  {/* Checkmark or Warning */}
                  <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                    item.complete ? 'bg-emerald-600' : 'bg-amber-500'
                  }`}>
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {item.complete ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 9v2m0 4h.01" />
                      )}
                    </svg>
                  </div>

                  {/* Item Details */}
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${
                      item.complete ? 'text-gray-900' : 'text-amber-900'
                    }`}>
                      {item.label}
                    </div>
                    <div className={`text-xs ${
                      item.complete ? 'text-gray-600' : 'text-amber-600'
                    }`}>
                      {item.details}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Full Experience Preview - SCRUM-208 */}
        <div className="border-t border-gray-200 pt-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Experience Preview</h3>
          <p className="text-sm text-gray-600 mb-4">
            This is how guests will see your experience listing.
          </p>

          {/* Preview Card */}
          <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            {/* Cover Photo — AC-07: uses API photos if available */}
            {displayCoverPhoto && (
              <div className="relative h-64" data-testid="preview-cover">
                <img
                  src={displayCoverPhoto.url}
                  alt={formData.experienceName}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 bg-emerald-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  New Experience
                </div>
              </div>
            )}

            {/* Content */}
            <div className="p-6">
              {/* Title and Price */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">
                    {formData.experienceName || 'Experience Name'}
                  </h4>
                  {formData.categoryL1Id && (
                    <div className="text-sm text-gray-600">
                      Category ID: {formData.categoryL1Id} (L1), {formData.categoryL2Id} (L2)
                    </div>
                  )}
                </div>
                <div className="text-right">
                  {formData.pricingModel === 'per_person' && formData.perPersonPriceCents > 0 && (
                    <div className="text-2xl font-bold text-emerald-700">
                      {formatPrice(formData.perPersonPriceCents)}
                      <span className="text-sm text-gray-600 font-normal">/person</span>
                    </div>
                  )}
                  {formData.pricingModel === 'flat_group' && formData.basePriceCents > 0 && (
                    <div className="text-2xl font-bold text-emerald-700">
                      {formatPrice(formData.basePriceCents)}
                      <span className="text-sm text-gray-600 font-normal">/group</span>
                    </div>
                  )}
                  {formData.pricingModel === 'base_plus_per' && formData.basePriceCents > 0 && (
                    <div className="text-2xl font-bold text-emerald-700">
                      {formatPrice(formData.basePriceCents)}
                      <span className="text-sm text-gray-600 font-normal"> base</span>
                    </div>
                  )}
                  {formData.pricingModel === 'base_covers_n' && formData.basePriceCents > 0 && (
                    <div data-testid="review-base-covers-n">
                      <div className="text-2xl font-bold text-emerald-700">
                        {formatPrice(formData.basePriceCents)}
                        <span className="text-sm text-gray-600 font-normal"> base</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Covers {formData.includedCount || 2} guests, +{formatPrice(formData.perPersonPriceCents || 0)}/extra
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {formData.description && (
                <div className="mb-4">
                  <p className="text-gray-700 leading-relaxed">
                    {formData.description}
                  </p>
                </div>
              )}

              {/* Highlights */}
              {formData.highlights?.length > 0 && (
                <div className="mb-4">
                  <h5 className="text-sm font-semibold text-gray-900 mb-2">Highlights</h5>
                  <ul className="space-y-1">
                    {formData.highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                        <svg className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Details Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 py-4 border-t border-gray-200">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Duration</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {formData.durationHours ? `${formData.durationHours} hours` : 'Not set'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Group Size</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {formData.minGroupSize && formData.maxGroupSize
                      ? `${formData.minGroupSize}-${formData.maxGroupSize} guests`
                      : 'Not set'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Location</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {formData.meetingPointAddress || 'Not set'}
                  </div>
                </div>
              </div>

              {/* Gallery Photos — AC-07: uses API photos if available */}
              {displayGalleryPhotos.length > 0 && (
                <div className="border-t border-gray-200 pt-4" data-testid="preview-gallery">
                  <h5 className="text-sm font-semibold text-gray-900 mb-3">Gallery</h5>
                  <div className="grid grid-cols-3 gap-2">
                    {displayGalleryPhotos.slice(0, 6).map((photo, index) => (
                      <img
                        key={photo.assetId || index}
                        src={photo.url}
                        alt={photo.caption || `Gallery ${index + 1}`}
                        className="w-full h-24 object-cover rounded"
                      />
                    ))}
                  </div>
                  {displayGalleryPhotos.length > 6 && (
                    <p className="text-xs text-gray-500 mt-2">
                      +{displayGalleryPhotos.length - 6} more photos
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Terms Acknowledgment - SCRUM-210 */}
        <div className="border-t border-gray-200 pt-6 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <input
                id="termsAccepted"
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1 w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-2 focus:ring-emerald-500"
              />
              <label htmlFor="termsAccepted" className="flex-1 text-sm text-gray-700">
                I acknowledge that I have read and agree to the{' '}
                <a
                  href="/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-600 hover:text-emerald-700 font-medium underline"
                >
                  Terms of Service
                </a>
                {' '}and{' '}
                <a
                  href="/guide-policies"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-600 hover:text-emerald-700 font-medium underline"
                >
                  Guide Policies
                </a>
                . I confirm that all information provided is accurate and I have the necessary qualifications and insurance to offer this experience.
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Publish Actions - SCRUM-211 */}
      <FormActions
        onCancel={onBack}
        onSubmit={handlePublish}
        hasUnsavedChanges={true}
        isSaving={isPublishing}
        cancelLabel="Back"
        submitLabel={isPublishing
          ? (isNewExperience ? 'Publishing...' : 'Saving...')
          : (isNewExperience ? 'Publish Experience' : 'Save Changes')}
        showSaveDraft={false}
        disableSubmit={!stats.allRequiredComplete || !termsAccepted || isPublishing}
        submitVariant="success"
        layout="split"
      />

      {/* Warning if required fields missing */}
      {!stats.allRequiredComplete && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900 mb-1">
                Complete required fields to publish
              </p>
              <p className="text-xs text-amber-700">
                Go back to previous stages to fill in missing required information.
                You can still save as a draft and complete it later.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
